import React, { useEffect, useState } from "react";
import { Link } from "../router";
import { Shell } from "../components/Shell";
import { api, ApiError, descargarArchivo } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Icon } from "../icons";

type Turno = {
  id: number; caja: string; estado: string; fechaApertura: string; fechaCierre: string | null;
  baseInicial: number; totalEfectivo: number; totalTarjeta: number; totalTransferencia: number;
  conteoEfectivo: number | null; diferencia: number | null; observaciones: string | null;
  usuarioAperturaId: number | null; usuarioAperturaNombre: string | null;
  usuarioCierreId: number | null; usuarioCierreNombre: string | null;
};
type Venta = {
  id: number; numero: string; turnoCajaId: number; clienteId: number | null; clienteNombre: string;
  vendedorNombre: string | null;
  fecha: string; medioPago: string; base: number; ivaValor: number; retefuenteValor: number;
  reteicaValor: number; reteivaValor: number; valorTotal: number; netoRecibido: number; estado: string;
};

export const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
export const MEDIO_PAGO_LABEL: Record<string, string> = { efectivo: "Efectivo", tarjeta: "Tarjeta", transferencia: "Transferencia" };

export function Pos() {
  const { sesion, tienePermiso } = useAuth();
  const puedeCaja = tienePermiso("pos:caja");
  const puedeVender = tienePermiso("pos:vender");
  const puedeAnular = tienePermiso("pos:anular");

  // Puede haber varios cajeros vendiendo al mismo tiempo, cada uno en su
  // propia caja — por eso esta vista de back office ya no asume "el" turno
  // abierto: lista TODAS las cajas abiertas ahora mismo (con el nombre de
  // quién la abrió, por seguridad/trazabilidad) y deja elegir cuál mirar.
  const [turnosAbiertos, setTurnosAbiertos] = useState<Turno[] | undefined>(undefined);
  const [turnoSeleccionadoId, setTurnoSeleccionadoId] = useState<number | null>(null);
  const [ventas, setVentas] = useState<Venta[]>([]);
  const [historial, setHistorial] = useState<Turno[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modalAbrir, setModalAbrir] = useState(false);
  const [turnoACerrar, setTurnoACerrar] = useState<Turno | null>(null);

  const cargar = async (mantenerSeleccion = false) => {
    setError(null);
    try {
      const [abiertos, todos] = await Promise.all([api.get("/pos/turnos/abiertos"), api.get("/pos/turnos")]);
      setTurnosAbiertos(abiertos);
      setHistorial(todos.filter((x: Turno) => x.estado === "cerrado"));
      setTurnoSeleccionadoId((actual) => {
        if (mantenerSeleccion && actual && abiertos.some((t: Turno) => t.id === actual)) return actual;
        const mio = abiertos.find((t: Turno) => t.usuarioAperturaId === sesion?.usuario.id);
        return (mio || abiertos[0])?.id ?? null;
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la información de caja.");
    }
  };
  useEffect(() => { cargar(); }, []);

  useEffect(() => {
    if (!turnoSeleccionadoId) { setVentas([]); return; }
    api.get(`/pos/ventas?turnoId=${turnoSeleccionadoId}`).then(setVentas).catch(() => setVentas([]));
  }, [turnoSeleccionadoId]);

  const turnoSeleccionado = turnosAbiertos?.find((t) => t.id === turnoSeleccionadoId) || null;
  // El propio turno del usuario que está viendo esta pantalla (no cualquier
  // caja abierta por otro cajero) — es la condición real para poder vender
  // en /pos/mostrador. Ver el comentario junto al botón "Ir al mostrador".
  const miTurnoAbierto = !!turnosAbiertos?.some((t) => t.usuarioAperturaId === sesion?.usuario.id);

  const anular = async (ventaId: number) => {
    try { await api.post(`/pos/ventas/${ventaId}/anular`, {}); cargar(true); } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo anular la venta.");
    }
  };

  return (
    <Shell titulo="POS — Punto de venta" breadcrumb="POS (mostrador)">
      <div className="card p-5 mb-4 flex items-center justify-between gap-4 bg-gradient-to-r from-navy to-navy-light text-white border-none">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            <Icon name="creditCard" className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-sm font-bold">Terminal de mostrador</h3>
            <p className="text-xs text-white/70 mt-0.5">Pantalla dedicada para ventas rápidas: productos, cliente y cobro en un solo lugar.</p>
          </div>
        </div>
        {/* Bug real corregido (reportado por el usuario: "el botón al mostrador
            no debe activarse sin que esté una caja abierta"): este botón
            navegaba a /pos/mostrador sin importar si el usuario tenía un
            turno de caja propio abierto. PosMostrador.tsx ya se defendía de
            esto mostrando una pantalla de "no hay turno abierto" en vez de
            dejar vender — pero llegar ahí para toparse con un callejón sin
            salida es mala UX. Ahora el botón solo se activa si el usuario ya
            tiene SU PROPIO turno abierto (turnosAbiertos, cargado arriba,
            trae el turno_apertura de cada caja); si no tiene uno, se le pide
            abrir caja desde aquí mismo ("+ Abrir otra caja", visible con el
            permiso pos:caja) en vez de ofrecer un botón que no lleva a
            ninguna venta real. El backend (`registrar_venta` en
            pos_utils.py) de todos modos sigue validando el turno de forma
            independiente en cada venta — esto es solo la UI. */}
        {puedeVender && miTurnoAbierto ? (
          <Link to="/pos/mostrador" className="btn-primary bg-white text-navy hover:bg-white/90 whitespace-nowrap shrink-0">
            Ir al mostrador →
          </Link>
        ) : puedeVender ? (
          <span
            className="text-xs text-white/70 whitespace-nowrap border border-white/25 rounded-lg px-3 py-2 cursor-not-allowed select-none"
            title="Abra su turno de caja (abajo, '+ Abrir otra caja') antes de poder vender en el mostrador."
          >
            Abra una caja para vender
          </span>
        ) : (
          <span className="text-xs text-white/60 whitespace-nowrap">Sin permiso para vender</span>
        )}
      </div>

      <p className="text-sm text-textmuted mb-4">
        Ventas directas de mostrador agrupadas por turno de caja. Cada venta reutiliza el mismo cálculo de IVA y
        retenciones por cliente y el mismo kardex de producto terminado que ya usa la facturación de Comercial —
        con cliente identificado (agente retenedor/gran contribuyente) se retiene igual que en una factura; sin
        cliente ("consumidor final") no aplica ninguna retención.
      </p>
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}

      {turnosAbiertos === undefined && <p className="text-sm text-textmuted">Cargando...</p>}

      {turnosAbiertos !== undefined && (
        <>
          <div className="flex justify-between items-end mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Cajas abiertas ahora</h3>
              <p className="text-xs text-textmuted mt-0.5">
                Puede haber varios cajeros vendiendo al mismo tiempo, cada uno en su propia caja — por seguridad,
                cada una muestra quién la abrió.
              </p>
            </div>
            {puedeCaja && <button className="btn-secondary shrink-0" onClick={() => setModalAbrir(true)}>+ Abrir otra caja</button>}
          </div>

          {turnosAbiertos.length === 0 ? (
            <div className="card p-6 text-center mb-6">
              <p className="text-sm text-textmuted">No hay ninguna caja abierta en este momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {turnosAbiertos.map((t) => {
                const esMia = t.usuarioAperturaId === sesion?.usuario.id;
                const seleccionada = t.id === turnoSeleccionadoId;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTurnoSeleccionadoId(t.id)}
                    className={`card p-4 text-left transition-all hover:shadow-card-hover ${seleccionada ? "border-accent ring-1 ring-accent/25" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800">{t.caja}</h4>
                        <p className="text-[11px] text-textmuted mt-0.5">Abierto {t.fechaApertura}</p>
                      </div>
                      {esMia && <span className="badge bg-accent-light text-accent-dark shrink-0">Tu caja</span>}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-600 mb-3">
                      <Icon name="user" className="h-3.5 w-3.5 text-textmuted" />
                      {t.usuarioAperturaNombre || "—"}
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 mb-3">
                      <div className="rounded-md bg-surface px-2 py-1.5">
                        <div className="text-[10px] text-textmuted">Efectivo</div>
                        <div className="text-xs font-semibold text-slate-800">{money(t.totalEfectivo)}</div>
                      </div>
                      <div className="rounded-md bg-surface px-2 py-1.5">
                        <div className="text-[10px] text-textmuted">Tarjeta</div>
                        <div className="text-xs font-semibold text-slate-800">{money(t.totalTarjeta)}</div>
                      </div>
                      <div className="rounded-md bg-surface px-2 py-1.5">
                        <div className="text-[10px] text-textmuted">Transf.</div>
                        <div className="text-xs font-semibold text-slate-800">{money(t.totalTransferencia)}</div>
                      </div>
                    </div>
                    {puedeCaja && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="text-xs font-semibold text-navy hover:underline"
                        onClick={(e) => { e.stopPropagation(); setTurnoACerrar(t); }}
                      >
                        Cerrar esta caja
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {turnoSeleccionado && (
            <>
              <h3 className="text-sm font-semibold text-slate-800 mb-3">
                Ventas de "{turnoSeleccionado.caja}" ({turnoSeleccionado.usuarioAperturaNombre})
              </h3>
              <div className="card overflow-hidden mb-8">
                <table className="w-full">
                  <thead><tr className="border-b border-borderc">
                    <th className="table-th">N°</th><th className="table-th">Cliente</th><th className="table-th">Vendedor</th>
                    <th className="table-th">Medio de pago</th>
                    <th className="table-th">Base</th><th className="table-th">Retenciones</th><th className="table-th">Total</th>
                    <th className="table-th">Estado</th><th className="table-th"></th>
                  </tr></thead>
                  <tbody>
                    {ventas.map((v) => {
                      const retenciones = v.retefuenteValor + v.reteicaValor + v.reteivaValor;
                      return (
                        <tr key={v.id} className="border-b border-borderc last:border-0">
                          <td className="table-td font-mono text-xs font-semibold">{v.numero}</td>
                          <td className="table-td">{v.clienteNombre}</td>
                          <td className="table-td text-xs">{v.vendedorNombre || "—"}</td>
                          <td className="table-td">{MEDIO_PAGO_LABEL[v.medioPago] || v.medioPago}</td>
                          <td className="table-td">{money(v.base)}</td>
                          <td className="table-td">{retenciones > 0 ? money(retenciones) : "—"}</td>
                          <td className="table-td font-semibold">{money(v.valorTotal)}</td>
                          <td className="table-td">
                            <span className={`badge ${v.estado === "anulada" ? "bg-red-50 text-red-700" : "bg-accent-light text-accent-dark"}`}>{v.estado}</span>
                          </td>
                          <td className="table-td text-right space-x-2 whitespace-nowrap">
                            <button
                              className="text-navy hover:underline text-xs font-semibold"
                              onClick={() => descargarArchivo(`/pos/ventas/${v.id}/documento-equivalente`, `documento_equivalente_${v.numero}.xml`)}
                            >
                              Doc. equivalente
                            </button>
                            {v.estado !== "anulada" && (
                              <Link
                                to={`/notas?documentoOrigen=venta_pos&documentoId=${v.id}`}
                                className="text-accent-dark hover:underline text-xs font-semibold"
                              >
                                Nota
                              </Link>
                            )}
                            {puedeAnular && v.estado === "cerrada" && (
                              <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => anular(v.id)}>Anular</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {ventas.length === 0 && (
                      <tr><td colSpan={9} className="table-td text-center text-textmuted py-6">Sin ventas registradas en esta caja todavía.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {historial.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Turnos anteriores</h3>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-borderc">
                <th className="table-th">Caja</th><th className="table-th">Abierto por</th>
                <th className="table-th">Apertura</th><th className="table-th">Cerrado por</th><th className="table-th">Cierre</th>
                <th className="table-th">Base</th><th className="table-th">Efectivo</th><th className="table-th">Tarjeta</th>
                <th className="table-th">Transferencia</th><th className="table-th">Diferencia</th>
              </tr></thead>
              <tbody>
                {historial.map((t) => (
                  <tr key={t.id} className="border-b border-borderc last:border-0">
                    <td className="table-td">{t.caja}</td>
                    <td className="table-td text-xs">{t.usuarioAperturaNombre || "—"}</td>
                    <td className="table-td text-xs">{t.fechaApertura}</td>
                    <td className="table-td text-xs">{t.usuarioCierreNombre || "—"}</td>
                    <td className="table-td text-xs">{t.fechaCierre}</td>
                    <td className="table-td">{money(t.baseInicial)}</td>
                    <td className="table-td">{money(t.totalEfectivo)}</td>
                    <td className="table-td">{money(t.totalTarjeta)}</td>
                    <td className="table-td">{money(t.totalTransferencia)}</td>
                    <td className="table-td">
                      <span className={`badge ${t.diferencia === 0 ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>
                        {money(t.diferencia || 0)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalAbrir && <ModalAbrirTurno onClose={() => setModalAbrir(false)} onListo={() => { setModalAbrir(false); cargar(); }} />}
      {turnoACerrar && (
        <ModalCerrarTurno
          turno={turnoACerrar}
          onClose={() => setTurnoACerrar(null)}
          onListo={() => { setTurnoACerrar(null); cargar(true); }}
        />
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- abrir turno
function ModalAbrirTurno({ onClose, onListo }: { onClose: () => void; onListo: () => void }) {
  const [caja, setCaja] = useState("Caja mostrador");
  const [baseInicial, setBaseInicial] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const abrir = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/pos/turnos/abrir", { caja, baseInicial });
      onListo();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo abrir el turno.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={abrir} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Abrir turno de caja</h3>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Caja</label>
          <input className="input-field" value={caja} onChange={(e) => setCaja(e.target.value)} required /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Base inicial (efectivo con el que abre)</label>
          <input className="input-field" type="number" min={0} step="1000" value={baseInicial} onChange={(e) => setBaseInicial(Number(e.target.value))} /></div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Abriendo..." : "Abrir turno"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- cerrar turno
function ModalCerrarTurno({ turno, onClose, onListo }: { turno: Turno; onClose: () => void; onListo: () => void }) {
  const esperado = turno.baseInicial + turno.totalEfectivo;
  const [conteoEfectivo, setConteoEfectivo] = useState(esperado);
  const [observaciones, setObservaciones] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const diferencia = Math.round((conteoEfectivo - esperado) * 100) / 100;

  const cerrar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/pos/turnos/${turno.id}/cerrar`, { conteoEfectivo, observaciones });
      onListo();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cerrar el turno.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={cerrar} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Cerrar turno de caja — arqueo</h3>
        <div className="rounded-md bg-navy/5 border border-navy/10 px-3 py-2 text-sm flex justify-between text-navy">
          <span>Efectivo esperado (base + ventas en efectivo)</span><span className="font-semibold">{money(esperado)}</span>
        </div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Conteo físico de efectivo</label>
          <input className="input-field" type="number" step="100" value={conteoEfectivo} onChange={(e) => setConteoEfectivo(Number(e.target.value))} required /></div>
        <div className={`rounded-md px-3 py-2 text-sm flex justify-between font-semibold ${diferencia === 0 ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>
          <span>Diferencia</span><span>{money(diferencia)}</span>
        </div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones (opcional)</label>
          <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Cerrando..." : "Cerrar turno"}</button>
        </div>
      </form>
    </div>
  );
}

